import React, {
    cloneElement,
    useEffect,
    useRef,
    useState,
} from 'react';

import PropTypes from 'prop-types';
import cx from 'classnames';

import { Flex } from '@zillow/constellation';

import Button from '@zg-rentals/multibrand-ui/lib/Button';
import Link from '@zg-rentals/multibrand-ui/lib/Link';
import Radio from '@zg-rentals/multibrand-ui/lib/Radio';
import SpacerRow from '@zg-rentals/multibrand-ui/lib/SpacerRow';
import Text from '@zg-rentals/multibrand-ui/lib/Text';

import ListingActivationTerms from 'app/shared/components/ListingActivationTerms';
import SpinnerContainer from 'app/shared/components/SpinnerContainer';

import PaidInclusionConstants from 'app/shared/constants/PaidInclusionConstants';

import zGuaranteeIcon from 'app/shared/images/z-guarantee.svg';

import './style.scss';
import { StyledLeadGuaranteeBanner } from './StyledComponents';

const { PAYMENT_METHOD } = PaidInclusionConstants;

const defaultProps = {
    defaultCreditCard: () => {},
};

const SelectPaymentMethod = ({
    creditCardCapture,
    defaultCreditCard,
    defaultPaymentMethodSuccess,
    displaySelectPaymentMethodModal,
    isEligibleForLeadGuarantee,
    isIframeLoaded,
    isRenderedInPageView,
    onPaymentMethodChange,
    onRefreshDefaultPaymentMethod,
    price,
    setCurrentPhase,
}) => {
    const creditCardCaptureRef = useRef(null);
    const [paymentMethod, setPaymentMethod] = useState(defaultPaymentMethodSuccess ? PAYMENT_METHOD.DEFAULT :
        PAYMENT_METHOD.NEW);
    const [showSpinner, setShowSpinner] = useState(false);

    useEffect(() => {
        const shouldPerformScrollEffect = creditCardCaptureRef.current && isIframeLoaded
            && paymentMethod === PAYMENT_METHOD.NEW;
        if (shouldPerformScrollEffect) {
            // class is set in EditListingPage
            // dialog body is set via constellation ModalDialog component
            const modalBodyQuery = '.PaidInclusionPaymentModal [class^="DialogBody"]';

            // if SelectPaymentMethod is being rendered for mobile web view, scroll at the window level.
            const ref = isRenderedInPageView
                ? window
                : document.querySelector(modalBodyQuery);

            const creditCardCaptureOffsetTop = creditCardCaptureRef.current.offsetTop - 70;

            ref.scrollTo({
                top: creditCardCaptureOffsetTop,
                behavior: 'smooth',
            });
        }
    }, [isIframeLoaded, paymentMethod, isRenderedInPageView]);

    const onRefreshDefaultPaymentMethodClick = () => {
        setShowSpinner(true);
        onRefreshDefaultPaymentMethod().then(() => {
            setShowSpinner(false);
        });
    };


    const { maskedNumber, cardBrand } = defaultCreditCard || {};
    const isDefaultPaymentMethodSelected = paymentMethod === PAYMENT_METHOD.DEFAULT;
    const isNewPaymentMethodSelected = paymentMethod === PAYMENT_METHOD.NEW;
    const renderPaymentMethods = () => (
        <React.Fragment>
            <SpacerRow className={cx('SpacerRow-radio', {
                checked: isDefaultPaymentMethodSelected,
            })}
            >
                <Radio
                    data-test="default-payment-method-radio"
                    disabled={!defaultPaymentMethodSuccess}
                    label={!defaultPaymentMethodSuccess ?
                        'Default payment method'
                        :
                        (
                            <React.Fragment>
                                {/* note: MUI's Radio.propTypes.label define string as prop */}
                                {/* but it's rendered as a child within a div, ignore console warning for now */}
                                {maskedNumber}
                                <strong className="card-brand">{cardBrand}</strong>
                            </React.Fragment>
                        )}
                    id={PAYMENT_METHOD.DEFAULT}
                    onChange={(method) => {
                        setPaymentMethod(method);
                        onPaymentMethodChange(method);
                    }}
                    checked={isDefaultPaymentMethodSelected}
                />
                {!defaultPaymentMethodSuccess ?
                    (
                        <SpacerRow>
                            <SpacerRow>
                                There was an error retrieving your default payment method.
                            </SpacerRow>
                            <Link onClick={() => onRefreshDefaultPaymentMethodClick()}>
                                Click here to refresh.
                            </Link>
                        </SpacerRow>
                    ) :
                    (<div />)
                }
                {showSpinner && (<SpinnerContainer size="md" />)}
            </SpacerRow>
            <SpacerRow className={cx('SpacerRow-radio', {
                checked: isNewPaymentMethodSelected,
            })}
            >
                <Radio
                    data-test="new-payment-method-radio"
                    label="Replace with new card"
                    id={PAYMENT_METHOD.NEW}
                    onChange={(method) => {
                        setPaymentMethod(method);
                        onPaymentMethodChange(method);
                    }}
                    checked={isNewPaymentMethodSelected}
                />
                {isNewPaymentMethodSelected &&
                    // select new payment method
                    // step: capture credit card
                    cloneElement(creditCardCapture, {
                        ref: creditCardCaptureRef,
                    })
                }
            </SpacerRow>
        </React.Fragment>
    );

    if (!displaySelectPaymentMethodModal) {
        return creditCardCapture;
    }

    return (
        <div className="SelectPaymentMethod">
            <SpacerRow>
                <Text type="h2">Checkout</Text>
            </SpacerRow>
            <SpacerRow size="md">
                <Text type="body">
                    Your listing will renew automatically until you deactivate it. Your card will be charged
                    <strong>
                        {` ${price} per week `}
                    </strong>
                    while your listing is active on the Zillow Rental Network.
                </Text>

                {isEligibleForLeadGuarantee && (
                    <StyledLeadGuaranteeBanner>
                        <Flex
                            display="flex"
                            direction="row"
                            alignItems="center"
                            justifyContent="center"
                        >
                            <img alt="" src={zGuaranteeIcon} />
                            <Text fontType="body">Includes Lead Guarantee</Text>
                        </Flex>
                    </StyledLeadGuaranteeBanner>
                )}
            </SpacerRow>
            <SpacerRow>
                <Text type="h3">Payment method</Text>
            </SpacerRow>
            {renderPaymentMethods()}
            {isDefaultPaymentMethodSelected && (
                <div className="ModalActions">
                    <ListingActivationTerms lowlight={true} actionText="By clicking Publish listing" />
                    <SpacerRow>
                        <Button
                            data-test="checkout-button"
                            type="primary"
                            full={true}
                            onClick={() => setCurrentPhase(PaidInclusionConstants.PAYMENT_PHASE.CREDIT_CARD_CHARGE)}
                        >
                            Publish listing
                        </Button>
                    </SpacerRow>
                </div>
            )}
        </div>
    );
};

SelectPaymentMethod.defaultProps = defaultProps;

SelectPaymentMethod.propTypes = {
    creditCardCapture: PropTypes.node.isRequired,
    defaultCreditCard: PropTypes.shape({
        cardBrand: PropTypes.string,
        defaultPaymentMethodExists: PropTypes.bool.isRequired,
        expirationMonth: PropTypes.string,
        expirationYear: PropTypes.string,
        maskedNumber: PropTypes.string,
    }),
    isIframeLoaded: PropTypes.bool.isRequired,
    onPaymentMethodChange: PropTypes.func.isRequired,
    onRefreshDefaultPaymentMethod: PropTypes.func.isRequired,
};

export default SelectPaymentMethod;
